import { memo, ReactNode, useCallback } from 'react'
import { ProductsSummaryProps } from './types'
import { PRODUCT_EXTRA_OPTIONS, PRODUCT_COMPARISON_KEYS, ProductComparisonKeys, ProductExtraOptions } from './constants'
import styles from './ProductsSummary.module.scss'
import { Trans, useTranslation } from 'next-i18next/pages'
import { formatAmount } from 'utils/format'
import { useGetLanguage } from 'hooks/useGetLanguage'
import { Button, Heading, Icon, List, Tag, Tooltip } from '@boltenergy-be/design-system'
import { startSimulationOrRegistration } from 'utils/customerFlow'
import { Product, PRODUCTS_CONFIG, PRODUCT_CONFIG_OPTIONS, PRODUCTS, REGIONS } from '@boltenergy-be/shared'
import parse from 'html-react-parser'
import Link from 'components/link/Link'
import { routes } from 'utils/routes'

const ProductsSummary = memo(
  ({ customerType, openDrawer, products, extraStartSimulationAction, region, platformCostReduction }: ProductsSummaryProps) => {
    // i18n
    const { t } = useTranslation(['pricing', 'common'])
    const language = useGetLanguage()
    const uppercaseLanguage = useGetLanguage('uppercase')

    /**
     * Get the text for the product comparison based on the given key and product
     * @type {(key: ProductComparisonKeys, product: Product) => (string | null)}
     */
    const getProductComparisonText = useCallback(
      (key: ProductComparisonKeys, product: Product): string | ReactNode | null => {
        const value = PRODUCTS_CONFIG[product][key]

        switch (key) {
          case PRODUCT_CONFIG_OPTIONS.type: {
            return t(($) => $.comparison.primary.type, { context: value })
          }

          case PRODUCT_CONFIG_OPTIONS.platformCost: {
            const initialPlatformCost = value[customerType]
            const hasPriceReduction = platformCostReduction && !isNaN(Number(platformCostReduction))
            const platformCost = hasPriceReduction
              ? initialPlatformCost - initialPlatformCost * Number(platformCostReduction)
              : initialPlatformCost
            return t(($) => $.comparison.primary.platformCost, {
              cost: formatAmount(initialPlatformCost, language),
              reduced: formatAmount(platformCost, language),
              context: hasPriceReduction ? 'reduced' : 'normal'
            })
          }

          case PRODUCT_CONFIG_OPTIONS.directDebitMandatory: {
            return t(($) => $.comparison.primary.directDebit, { context: value ? 'mandatory' : 'optional' })
          }

          case PRODUCT_CONFIG_OPTIONS.contactOptions: {
            const digitalOnly = PRODUCTS_CONFIG[product].digitalOnly[PRODUCT_CONFIG_OPTIONS.contactOptions]
            return t(($) => $.comparison.primary.contact, { context: digitalOnly ? 'online' : 'all' })
          }

          case PRODUCT_CONFIG_OPTIONS.billingOptions: {
            const digitalOnly = PRODUCTS_CONFIG[product].digitalOnly[PRODUCT_CONFIG_OPTIONS.billingOptions]
            return t(($) => $.comparison.primary.billing, { context: digitalOnly ? 'digital' : 'all' })
          }

          case 'bestInjection': {
            return (
              <span>
                <Trans
                  {...{ t }}
                  i18nKey={($) => $.comparison.primary.injection}
                  components={[<Link key="0" variant="inline" href={routes(language).injectionTariff} />]}
                />
              </span>
            )
          }

          default: {
            return null
          }
        }
      },
      [t, customerType, platformCostReduction, language]
    )

    /**
     * Get the text for the extra options based on the given key and product
     * @type {(key: ProductExtraOptions, product: Product) => (React.ReactNode | null)}
     */
    const getExtraOptionsText = useCallback(
      (key: ProductExtraOptions, product: Product): ReactNode | null => {
        switch (key) {
          case 'dynamicTariff': {
            const isVariableProduct = PRODUCTS_CONFIG[product].type === 'variable'
            return isVariableProduct ? (
              <span className={styles['with-tooltip']}>
                {t(($) => $.comparison.options.dynamicTariff.text)}{' '}
                <Tooltip tooltipContent={t(($) => $.comparison.options.dynamicTariff.tooltip)}>
                  <Icon name="questionMarkCircle" size={20} />
                </Tooltip>
              </span>
            ) : null
          }

          case 'monthlyBilling': {
            return region === REGIONS.flanders ? t(($) => $.comparison.options.monthlyBilling) : null
          }

          default: {
            return null
          }
        }
      },
      [region, t]
    )

    return (
      <ul className={styles['product-list']}>
        {products.map((product) => {
          const extraOptions = PRODUCT_EXTRA_OPTIONS.flatMap((option) => {
            const content = getExtraOptionsText(option, product)
            return content ? (
              <li key={`${product}-${option}`}>{typeof content === 'string' ? <span>{parse(content)}</span> : content}</li>
            ) : (
              []
            )
          })

          return (
            <li className={styles.product} key={`${product}-${region}`}>
              <header className={styles['product-header']}>
                <Heading as="h2" variant="h3" serif className={styles['product-name']}>
                  {PRODUCTS_CONFIG[product].name[uppercaseLanguage]}
                </Heading>

                {(product === PRODUCTS.variableOffline || product === PRODUCTS.plentyVariableOffline) && region === REGIONS.flanders && (
                  <Tag type="warm" size="small">
                    {t(($) => $.recommended, { ns: 'common' })}
                  </Tag>
                )}
              </header>

              <List
                as="ul"
                bullets={(index: number) => {
                  const key = PRODUCT_COMPARISON_KEYS[index]
                  const isOnlineProduct = product === PRODUCTS.variableOnline || product === PRODUCTS.plentyVariableOnline
                  if (isOnlineProduct && (key === 'billingOptions' || key === 'contactOptions')) return 'minus'
                  return 'check'
                }}
                className={styles.list}
              >
                {PRODUCT_COMPARISON_KEYS.flatMap((option) => {
                  const content = getProductComparisonText(option, product)
                  return content ? (
                    <li key={`${product}-${option}`}>{typeof content === 'string' ? <span>{parse(content)}</span> : content}</li>
                  ) : (
                    []
                  )
                })}
              </List>

              {!!extraOptions?.length && (
                <>
                  <Heading as="h3" variant="h5" className={styles['extra-options-title']}>
                    {t(($) => $.comparison.options.title)}
                  </Heading>

                  <List as="ul" bullets="check" className={styles.list}>
                    {extraOptions}
                  </List>
                </>
              )}

              <footer className={styles['product-footer']}>
                <Button
                  isFullwidth
                  type="button"
                  className="mb-400"
                  onClick={async () => {
                    await extraStartSimulationAction?.()
                    void startSimulationOrRegistration({
                      specificProduct: product
                    })
                  }}
                >
                  {t(($) => $.comparison.actions.choose)}
                </Button>

                <Button type="button" isFullwidth onClick={() => openDrawer(product)} representation="link">
                  {t(($) => $.comparison.actions.details)}
                </Button>
              </footer>
            </li>
          )
        })}
      </ul>
    )
  }
)

ProductsSummary.displayName = 'ProductsSummary'

export default ProductsSummary
